import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Check } from 'lucide-react';
import { CAPABILITIES } from '../../data/capabilities';
import usePricing, { priceLine } from '../../hooks/usePricing';
import { EASE_CSS } from '../../motion/tokens';

// Service rows. The sticky right panel shows what the active row includes and
// the catalog entry it maps to (portfolio imagery stays in the work reel).
export default function Capabilities() {
  const [active, setActive] = useState(CAPABILITIES[0].id);
  const rowRefs = useRef({});
  const { byId } = usePricing();
  const current = CAPABILITIES.find((c) => c.id === active) || CAPABILITIES[0];
  const plan = byId[current.planId];

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.dataset.id);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    Object.values(rowRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const panel = (c, p) => (
    <div className="flex h-full flex-col justify-between p-8">
      <div>
        <p className="text-sm text-sky">What is included</p>
        <ul className="mt-5 space-y-4">
          {c.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-lg text-cream/90">
              <Check className="mt-1.5 h-4 w-4 shrink-0 text-sky" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      {p ? (
        <Link to={'/pricing#' + p.id} className="mt-8 flex items-baseline justify-between border-t border-line pt-6 text-cream hover:text-sky">
          <span className="text-sm">{p.name}</span>
          <span className="price text-2xl text-amber">{priceLine(p)}</span>
        </Link>
      ) : null}
    </div>
  );

  return (
    <section className="border-t border-line py-16 md:py-24" id="capabilities">
      <div className="container-x">
        <h2 className="reveal display-lg max-w-[16ch]">Websites, SEO, AI and connected systems. Built by one team.</h2>
        <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <ol className="divide-y divide-line border-y border-line">
            {CAPABILITIES.map((c) => {
              const on = active === c.id;
              const titleCls = 'font-display text-2xl font-semibold tracking-tight transition-colors md:text-4xl ' + (on ? 'text-cream' : 'text-mist group-hover:text-cream');
              const arrowCls = 'mt-2 h-6 w-6 shrink-0 transition-all ' + (on ? 'text-sky' : 'text-dim opacity-0 group-hover:opacity-100');
              return (
                <li
                  key={c.id}
                  ref={(el) => { rowRefs.current[c.id] = el; }}
                  data-id={c.id}
                  onMouseEnter={() => setActive(c.id)}
                  className="group py-7 md:py-8"
                >
                  <Link to={'/services#' + c.id} className="flex items-start justify-between gap-6">
                    <div>
                      <h3 className={titleCls}>{c.title}</h3>
                      <p className={'mt-2 max-w-md text-base transition-colors ' + (on ? 'text-mist' : 'text-dim')}>{c.line}</p>
                    </div>
                    <ArrowUpRight className={arrowCls} />
                  </Link>
                  <div className="mt-5 rounded-xl border border-line bg-deep lg:hidden">{panel(c, byId[c.planId])}</div>
                </li>
              );
            })}
          </ol>
          <div className="hidden lg:block">
            <div className="sticky top-28 min-h-[26rem] overflow-hidden rounded-2xl border border-line bg-deep">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  className="absolute inset-0"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4, ease: EASE_CSS }}
                >
                  {panel(current, plan)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
